import ContainerSimple from "../../../Components/ContainerSimple";
import Custom from "../../../Components/Buttons/Custom";
import InputPayment from "../../../Components/InputFields/InputPayment";
import { useState, useEffect } from "react";
import { isoToJalali } from "../../../helper/isoToJalali";
import { formatTradePrice } from "../../../helper/formatTradePrice";
import { API_BASE_URL } from "../../../config/api";
import { ErrorState, Spinner } from "../../../Components/AsyncState";

const PAYMENTS_ADMIN_BASE_PATH = `${API_BASE_URL}/api/v0/payment-payout/admin`;
const VALID_STATUSES = new Set(["pending", "completed", "rejected"]);

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "failed") return "rejected";
  return value;
}

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) return false;

  const rawValue = String(value).trim();
  if (!rawValue) return false;

  const numericValue = Number(rawValue.replace(/,/g, ""));
  return !Number.isFinite(numericValue) || numericValue !== 0;
}

function formatDisplayValue(value) {
  if (value === null || value === undefined) return "-";

  const rawValue = String(value).trim();
  return rawValue ? rawValue : "-";
}

function formatAmount(value) {
  return hasMeaningfulValue(value) ? formatTradePrice(value) : "-";
}

function formatIrrAmount(value) {
  if (!hasMeaningfulValue(value)) return "-";

  const numericValue = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(numericValue)) return "-";

  return `${numericValue.toLocaleString("en-US")} ریال`;
}

function formatPercent(value) {
  return hasMeaningfulValue(value) ? `${value}%` : "-";
}

function getStatusDisplayText(status) {
  const normalizedStatus = normalizeStatus(status);

  if (normalizedStatus === "completed") return "تکمیل شده";
  if (normalizedStatus === "pending") return "در انتظار";
  if (normalizedStatus === "rejected") return "رد شده";

  return formatDisplayValue(status);
}

function isNowpayment(paymentType) {
  return String(paymentType || "").toLowerCase() === "nowpayment";
}

function isDirectpay(paymentType) {
  return String(paymentType || "").toLowerCase() === "directpay";
}

function getFinalCostDisplay(depositData) {
  if (isDirectpay(depositData?.payment_type)) {
    return formatIrrAmount(depositData?.actual_amount_irr);
  }

  return formatAmount(depositData?.final_cost);
}

function normalizeDecimalValue(value) {
  if (value === null || value === undefined) return "";

  const rawValue = String(value).trim().replace(/,/g, "");
  if (!rawValue) return "";

  const matchedNumber = rawValue.match(/^(\d+)(?:\.(\d+))?$/);
  if (!matchedNumber) return rawValue;

  const [, integerPart, fractionalPart = ""] = matchedNumber;
  const normalizedIntegerPart = String(Number(integerPart));
  const normalizedFractionalPart = fractionalPart.replace(/0+$/, "");

  return normalizedFractionalPart
    ? `${normalizedIntegerPart}.${normalizedFractionalPart}`
    : normalizedIntegerPart;
}

function formatEditableAmount(value) {
  if (!hasMeaningfulValue(value)) return "";
  return normalizeDecimalValue(value);
}

function getErrorMessageFromPayload(payload, fallbackText) {
  if (!payload) return fallbackText;
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
}

async function authorizedFetch(url, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status !== 401) {
    return response;
  }

  const refreshResponse = await fetch(
    `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    },
  );

  if (!refreshResponse.ok) {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    window.location.href = "/signin";
    return null;
  }

  const refreshData = await refreshResponse.json();
  localStorage.setItem("access", refreshData.access);

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
}

export default function DepositModal({ onClose, depositCode, paymentType }) {
  // const handleCopy = async (e) => {
  //   const p = e.currentTarget.querySelector("p");
  //   if (!p) return;
  //   const text = p.innerText;
  //   try {
  //     await navigator.clipboard.writeText(text);
  //   } catch {
  //     const ta = document.createElement("textarea");
  //     ta.value = text;
  //     ta.style.position = "fixed";
  //     ta.style.opacity = "0";
  //     document.body.appendChild(ta);
  //     ta.select();
  //     document.execCommand("copy");
  //     document.body.removeChild(ta);
  //   }
  // };
  const [depositData, setDepositData] = useState(null);
  const [status, setStatus] = useState("");
  const [actualAmountUsd, setActualAmountUsd] = useState("");
  const [amountError, setAmountError] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState("");
  const isReadOnly = isNowpayment(depositData?.payment_type ?? paymentType);
  const isDirectPayment = isDirectpay(depositData?.payment_type ?? paymentType);
  const trackingCodeLabel = isDirectPayment
    ? "کد پیگیری تراکنش"
    : "کد پیگیری بلاکچین";

  useEffect(() => {
    const fetchDepositData = async () => {
      setDetailLoading(true); setDetailError("");
      try {
        const response = await authorizedFetch(
          `${PAYMENTS_ADMIN_BASE_PATH}/payments/${depositCode}/`,
          {
            method: "GET",
          },
        );

        if (!response) return;

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const normalizedData = {
          ...data,
          status: normalizeStatus(data?.status),
        };

        console.log("Account Data:", normalizedData);
        setDepositData(normalizedData);
        setStatus(normalizedData.status ?? "");
        setActualAmountUsd(formatEditableAmount(data?.actual_amount_usd));
        setAmountError("");
        setActionError("");
      } catch (error) {
        console.error("Error fetching account data:", error);
        setDetailError("خطا در دریافت جزئیات تراکنش");
      } finally {
        setDetailLoading(false);
      }
    };

    fetchDepositData();
  }, [depositCode]);

  const handleActualAmountChange = (e) => {
    setActualAmountUsd(e.target.value);
    if (amountError) setAmountError("");
    if (actionError) setActionError("");
  };

  const handleStatusChange = (nextStatus) => {
    setStatus(nextStatus);
    if (actionError) setActionError("");
  };

  const handleChange = async () => {
    const paymentCode = depositData?.code ?? depositCode;
    if (!paymentCode) {
      setActionError("کد تراکنش برای ثبت تغییرات یافت نشد.");
      return;
    }

    if (!VALID_STATUSES.has(status)) {
      setActionError("وضعیت معتبر را انتخاب کنید.");
      return;
    }

    const trimmedAmount = actualAmountUsd.trim();
    const payload = { status };

    setAmountError("");
    setActionError("");

    if (trimmedAmount) {
      const normalizedAmount = normalizeDecimalValue(trimmedAmount);
      if (!/^\d+(\.\d+)?$/.test(normalizedAmount)) {
        setAmountError("مبلغ پرداخت شده باید عدد صحیح یا اعشاری معتبر باشد.");
        return;
      }

      payload.actual_amount_usd = normalizedAmount;
    }

    setSubmitting(true);

    try {
      const response = await authorizedFetch(
        `${PAYMENTS_ADMIN_BASE_PATH}/verify_payment/${paymentCode}/`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      if (!response) return;

      let data = null;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        if (
          Array.isArray(data?.actual_amount_usd) &&
          typeof data.actual_amount_usd[0] === "string"
        ) {
          setAmountError(data.actual_amount_usd[0]);
        } else {
          setActionError(
            getErrorMessageFromPayload(data, "ثبت تغییرات با خطا مواجه شد."),
          );
        }
        return;
      }

      window.location.reload();
    } catch (error) {
      setActionError(error?.message || "ثبت تغییرات با خطا مواجه شد.");
    } finally {
      setSubmitting(false);
    }
  };

  if (detailLoading) return <div role="status" aria-label="در حال بارگذاری" className="flex min-h-72 items-center justify-center text-[#8080804d]"><Spinner /></div>;
  if (detailError) return <ErrorState message={detailError} className="min-h-72" />;
  if (!depositData) return <ErrorState message="اطلاعات تراکنش یافت نشد" className="min-h-72" />;

  return (
    <ContainerSimple>
      <div className="w-full rounded-lg border border-zi-border-dark flex overflow-hidden">
        {/* headers */}
        <div className="w-max bg-black/10 text-black/50 dark:text-zi-text-dark/50 divide-y divide-black/15 dark:divide-white/15 [&>*]:px-2.5 [&>*]:py-2 [&>*]:whitespace-nowrap">
          <p>کد تراکنش</p> <p>نوع تراکنش</p> <p>تاریخ ایجاد</p> <p>نام کاربر</p>
          <p>چالش مرتبط</p> <p>مبلغ خام</p> <p>مبلغ پس از تخفیف سامانه</p>{" "}
          <p>مبلغ پس از اعمال کد تخفیف</p>
          <p>مبلغ نهایی</p> <p>کد تخفیف</p> <p>درصد کد تخفیف</p>
          <p>{trackingCodeLabel}</p> <p>آدرس مقصد</p>
          <p className="bg-black/15 !border-t-2 !border-black dark:!border-zi-border-dark">
            وضعیت
          </p>{" "}
          {/* <p className="bg-black/15">تاریخ تکمیل</p> */}
          {/* <p className="bg-black/15">آدرس مبدأ</p>{" "} */}
          <p className="bg-black/15">مبلغ پرداخت شده</p>
        </div>
        {/* values */}
        <div className="flex-1 text-zi-text-light dark:text-zi-text-dark divide-y divide-black/15 dark:divide-white/15 [&>*]:px-2.5 [&>*]:py-2 overflow-x-hidden [&>*]:w-full [&>*]:overflow-x-auto [&>*]:whitespace-nowrap">
          <p>{depositData?.code ?? depositCode ?? "-"}</p> <p>واریز</p>{" "}
          <p>
            {depositData?.created_at
              ? isoToJalali(depositData?.created_at)
              : "-"}
          </p>{" "}
          <p>
            {formatDisplayValue(
              depositData?.user_full_name || depositData?.user_email,
            )}
          </p>
          <p>{formatDisplayValue(depositData?.challenge_name)}</p>
          <p>{formatAmount(depositData?.cost)}</p>
          <p>{formatAmount(depositData?.discount_cost)}</p>
          <p>{formatAmount(depositData?.coupon_cost)}</p>
          <p>{getFinalCostDisplay(depositData)}</p>
          <p>{formatDisplayValue(depositData?.coupon_name)}</p>
          <p>{formatPercent(depositData?.coupon_discount_percent)}</p>
          <p>{formatDisplayValue(depositData?.transaction_id)}</p>
          <p>{formatDisplayValue(depositData?.gateway?.address)}</p>
          {isReadOnly ? (
            <p className="!border-t-2 !border-black dark:!border-zi-border-dark">
              {getStatusDisplayText(status || depositData?.status)}
            </p>
          ) : (
            <InputPayment
              type="select"
              options={["تکمیل شده", "در انتظار", "رد شده"]}
              name="status"
              id="status"
              placeholder="انتخاب کنید"
              className="w-full !p-0 !border-t-2 !border-black dark:!border-zi-border-dark"
              svgName="select"
              svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
              inputClassName="w-full cursor-pointer px-2.5 py-2"
              state={status}
              setState={handleStatusChange}
              disabled={submitting}
            />
          )}
          {/* <InputPayment
            type="datetime-local"
            id="date"
            name="date"
            className="w-full !p-0"
            inputClassName="w-full px-2.5 py-[7px]"
            svgName="calendar"
            svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark fill-zi-text-light dark:fill-zi-text-dark"
          /> */}
          {/* <InputPayment
            type="text"
            id="blockchain"
            name="blockchain"
            className="w-full !p-0"
            inputClassName="w-full px-2.5 py-2"
            svgName="pen"
            svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
          /> */}
          {/* <InputPayment
            type="text"
            id="blockchain"
            name="blockchain"
            className="w-full !p-0"
            inputClassName="w-full px-2.5 py-2"
            svgName="pen"
            svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
          /> */}
          {isReadOnly ? (
            <p>{formatAmount(depositData?.actual_amount_usd)}</p>
          ) : (
            <InputPayment
              type="text"
              id="actual_amount_usd"
              name="actual_amount_usd"
              className="w-full !p-0"
              inputClassName="w-full px-2.5 py-2"
              svgName="pen"
              svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
              placeholder=""
              value={actualAmountUsd}
              onChange={handleActualAmountChange}
              disabled={submitting}
              inputMode="decimal"
            />
          )}
        </div>
      </div>

      {!isReadOnly && amountError && (
        <p className="mt-3 text-sm text-red-500">{amountError}</p>
      )}
      {!isReadOnly && actionError && (
        <p className="mt-2 text-sm text-red-500">{actionError}</p>
      )}

      <div className="w-full flex gap-2.5 mt-6">
        {!isReadOnly && (
          <>
            <>
              <Custom
                text="بستن"
                svg_name="xMark"
                svgClassName="stroke-zi-text-light dark:stroke-zi-text-dark"
                href=""
                className={`!rounded-lg !p-2.5 w-full text-zi-text-light dark:text-zi-text-dark ring-1 ring-zi-border-dark bg-transparent ${
                  submitting ? "pointer-events-none opacity-60" : ""
                }`}
                onClick={onClose}
              />
              <Custom
                text="ذخیره تغییرات"
                loading={submitting}
                disabled={submitting}
                svg_name="checkMark"
                svgClassName="stroke-zi-text-dark"
                href=""
                className={`!rounded-lg !p-2.5 w-full text-zi-text-dark bg-zi-gradient-button-blue ${
                  submitting ? "pointer-events-none opacity-60" : ""
                }`}
                textShadow={true}
                svgShadow={true}
                onClick={handleChange}
              />
            </>
          </>
        )}
      </div>
    </ContainerSimple>
  );
}
